import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../ui/select';
import { ConfirmModal } from '../ui/ConfirmModal';
import { Edit, Trash2, Plus, Save, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AboutManager = () => {
  const { toast } = useToast();
  const [section, setSection] = useState(null);
  const [features, setFeatures] = useState([]);
  const [editingSection, setEditingSection] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeature, setNewFeature] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, feature: null });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [sectionRes, featuresRes] = await Promise.all([
        axios.get(`${API_URL}/api/about-section`, { headers }),
        axios.get(`${API_URL}/api/about-features`, { headers })
      ]);
      
      setSection(sectionRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch about data",
        variant: "destructive"
      });
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateSection = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/about-section`, section, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "About section updated successfully"
      });
      setEditingSection(false);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update about section",
        variant: "destructive"
      });
    }
  };

  const handleCreateFeature = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/about-features`, newFeature, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Feature added successfully"
      });
      setNewFeature(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add feature",
        variant: "destructive"
      });
    }
  };

  const handleUpdateFeature = async (featureId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/about-features/${featureId}`, editingFeature, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Feature updated successfully"
      });
      setEditingFeature(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update feature",
        variant: "destructive"
      });
    }
  };

  const handleDeleteFeature = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/about-features/${deleteModal.feature.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Feature deleted successfully"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete feature",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* About Section Card */}
      <Card className="bg-black/50 border-green-500/30 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-green-400">About Section</h2>
          {!editingSection && (
            <Button 
              onClick={() => setEditingSection(true)}
              className="bg-green-500 hover:bg-green-600"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>

        {editingSection ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <Input
                value={section?.title || ''}
                onChange={(e) => setSection({ ...section, title: e.target.value })}
                className="bg-black/30 border-green-500/30 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Subtitle</label>
              <Input
                value={section?.subtitle || ''}
                onChange={(e) => setSection({ ...section, subtitle: e.target.value })}
                className="bg-black/30 border-green-500/30 text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <Textarea
                value={section?.description || ''}
                onChange={(e) => setSection({ ...section, description: e.target.value })}
                rows={4}
                className="bg-black/30 border-green-500/30 text-white"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdateSection} className="bg-green-500 hover:bg-green-600">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button 
                onClick={() => {
                  setEditingSection(false);
                  fetchData();
                }}
                variant="outline"
                className="border-gray-500 text-gray-300"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-gray-300">
            <p><strong>Title:</strong> {section?.title}</p>
            <p><strong>Subtitle:</strong> {section?.subtitle}</p>
            <p><strong>Description:</strong> {section?.description}</p>
          </div>
        )}
      </Card>

      {/* Features Section */}
      <Card className="bg-black/50 border-orange-500/30 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-orange-400">Features</h2>
          {!newFeature && (
            <Button 
              onClick={() => setNewFeature({ title: '', description: '', icon: 'Music2', order: features.length })}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          )}
        </div>

        {/* Add New Feature Form */}
        {newFeature && (
          <Card className="bg-black/30 border-green-500/30 p-4 mb-4">
            <h3 className="text-lg font-semibold text-green-400 mb-4">New Feature</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <Input
                  value={newFeature.title}
                  onChange={(e) => setNewFeature({ ...newFeature, title: e.target.value })}
                  className="bg-black/30 border-green-500/30 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <Textarea
                  value={newFeature.description}
                  onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
                  rows={3}
                  className="bg-black/30 border-green-500/30 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Icon</label>
                <Select value={newFeature.icon} onValueChange={(value) => setNewFeature({ ...newFeature, icon: value })}>
                  <SelectTrigger className="bg-black/30 border-green-500/30 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black/95 border-green-500/30 text-white">
                    <SelectItem value="Music2">Music2 (Production)</SelectItem>
                    <SelectItem value="Radio">Radio (Label)</SelectItem>
                    <SelectItem value="Disc3">Disc3 (Events)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                <Input
                  type="number"
                  value={newFeature.order}
                  onChange={(e) => setNewFeature({ ...newFeature, order: parseInt(e.target.value) })}
                  className="bg-black/30 border-green-500/30 text-white"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateFeature} className="bg-green-500 hover:bg-green-600">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={() => setNewFeature(null)} variant="outline" className="border-gray-500 text-gray-300">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Features List */}
        <div className="space-y-4">
          {features.map((feature) => (
            <Card key={feature.id} className="bg-black/30 border-orange-500/20 p-4">
              {editingFeature?.id === feature.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                    <Input
                      value={editingFeature.title}
                      onChange={(e) => setEditingFeature({ ...editingFeature, title: e.target.value })}
                      className="bg-black/30 border-orange-500/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                    <Textarea
                      value={editingFeature.description}
                      onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })}
                      rows={3}
                      className="bg-black/30 border-orange-500/30 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Icon</label>
                    <Select value={editingFeature.icon} onValueChange={(value) => setEditingFeature({ ...editingFeature, icon: value })}>
                      <SelectTrigger className="bg-black/30 border-orange-500/30 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black/95 border-orange-500/30 text-white">
                        <SelectItem value="Music2">Music2 (Production)</SelectItem>
                        <SelectItem value="Radio">Radio (Label)</SelectItem>
                        <SelectItem value="Disc3">Disc3 (Events)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Order</label>
                    <Input
                      type="number"
                      value={editingFeature.order}
                      onChange={(e) => setEditingFeature({ ...editingFeature, order: parseInt(e.target.value) })}
                      className="bg-black/30 border-orange-500/30 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleUpdateFeature(feature.id)} className="bg-orange-500 hover:bg-orange-600">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button 
                      onClick={() => setEditingFeature(null)} 
                      variant="outline" 
                      className="border-gray-500 text-gray-300"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{feature.title}</h3>
                      <p className="text-sm text-gray-400">Icon: {feature.icon} | Order: {feature.order}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setEditingFeature(feature)}
                        size="sm"
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDeleteModal({ isOpen: true, feature })}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-gray-300">{feature.description}</p>
                </div>
              )}
            </Card>
          ))}
        </div>
      </Card>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, feature: null })}
        onConfirm={handleDeleteFeature}
        title="Confirm Delete"
        message={`Are you sure you want to delete the feature "${deleteModal.feature?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
